import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Trash2 } from "lucide-react";
import { apiJson } from "@/lib/api";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ReportReadyImageEditor,
  type ReportReadyCropPlacement,
  type ReportReadySavedImage,
} from "./ReportReadyImageEditor";
import { normalizePreset } from "./report-ready-editor-utils";

interface ReportReadyImageDto {
  secureUrl: string | null;
  updatedAt: string;
  rotation: number;
  brightness: number;
  contrast: number;
  cropRecipe: {
    crop?: ReportReadyCropPlacement | null;
    preset?: string;
  };
  metadata: {
    editor?: string;
    editorDetails?: {
      preset?: string;
    };
  };
}

interface ReportReadyEditorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  sourceEvidenceAssetId: number;
  reportReadyImageId?: number;
  originalImageUrl: string;
  storeName?: string;
  operatorName?: string;
  onSaved: () => void;
}

export function ReportReadyEditorModal({
  open,
  onOpenChange,
  mode,
  sourceEvidenceAssetId,
  reportReadyImageId,
  storeName,
  operatorName,
  onSaved,
}: ReportReadyEditorModalProps) {
  const { toast } = useToast();
  const [editorSourceUrl, setEditorSourceUrl] = useState<string | null>(null);
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState<string | undefined>();
  const [reportCrop, setReportCrop] = useState<ReportReadyCropPlacement | null>(null);
  const [initialAdjustments, setInitialAdjustments] = useState<ReportReadySavedImage["adjustments"] | undefined>();
  const [sourceLoadError, setSourceLoadError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setEditorSourceUrl(null);
      setExpectedUpdatedAt(undefined);
      setReportCrop(null);
      setInitialAdjustments(undefined);
      setSourceLoadError(null);
      return;
    }

    let cancelled = false;
    setEditorSourceUrl(null);
    setExpectedUpdatedAt(undefined);
    setReportCrop(null);
    setInitialAdjustments(undefined);
    setSourceLoadError(null);

    void (async () => {
      try {
        if (mode === "edit") {
          if (!reportReadyImageId) throw new Error("Missing Report Ready image ID");
          const [imageResponse, sourceResponse] = await Promise.all([
            apiJson<{ success: boolean; data: { image: ReportReadyImageDto } }>(
              `/api/report-ready-images/${reportReadyImageId}`,
            ),
            apiJson<{ success: boolean; data: { secureUrl: string } }>(
              `/api/evidence-assets/${sourceEvidenceAssetId}/report-ready/source`,
            ),
          ]);
          if (!imageResponse.success || !sourceResponse.success || !sourceResponse.data.secureUrl) {
            throw new Error("Source image URL unavailable");
          }
          if (!cancelled) {
            const image = imageResponse.data.image;
            setEditorSourceUrl(sourceResponse.data.secureUrl);
            setExpectedUpdatedAt(image.updatedAt);
            setReportCrop(image.cropRecipe?.crop ?? null);
            setInitialAdjustments({
              rotation: image.rotation ?? 0,
              brightness: image.brightness ?? 0,
              contrast: image.contrast ?? 0,
              preset: normalizePreset(image.cropRecipe?.preset ?? image.metadata?.editorDetails?.preset),
            });
          }
          return;
        }

        const response = await apiJson<{ success: boolean; data: { secureUrl: string } }>(
          `/api/evidence-assets/${sourceEvidenceAssetId}/report-ready/source`,
        );
        if (!response.success || !response.data.secureUrl) {
          throw new Error("Source image URL unavailable");
        }
        if (!cancelled) setEditorSourceUrl(response.data.secureUrl);
      } catch {
        if (!cancelled) setSourceLoadError("The image could not be loaded for editing.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode, open, reportReadyImageId, sourceEvidenceAssetId]);

  const editorKey = useMemo(
    () => `${mode}-${sourceEvidenceAssetId}-${reportReadyImageId ?? "new"}-${editorSourceUrl ?? "loading"}`,
    [editorSourceUrl, mode, reportReadyImageId, sourceEvidenceAssetId],
  );

  const saveMutation = useMutation({
    mutationFn: async (image: ReportReadySavedImage) => {
      const payload = {
        editRecipe: {
          version: 1,
          crop: image.reportCrop,
          rotation: image.adjustments.rotation,
          brightness: image.adjustments.brightness,
          contrast: image.adjustments.contrast,
          preset: image.adjustments.preset,
        },
        editor: {
          name: "native",
          preset: image.adjustments.preset,
        },
        expectedUpdatedAt: mode === "edit" ? expectedUpdatedAt : undefined,
      };

      const url = mode === "create"
        ? `/api/evidence-assets/${sourceEvidenceAssetId}/report-ready`
        : `/api/report-ready-images/${reportReadyImageId}`;
      const response = await apiRequest(url, mode === "create" ? "POST" : "PATCH", payload);
      return response.json();
    },
    onSuccess: () => {
      toast({ title: mode === "create" ? "Report Ready version created" : "Report Ready version saved" });
      onSaved();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
    },
  });

  const handleSave = async (image: ReportReadySavedImage) => {
    try {
      await saveMutation.mutateAsync(image);
    } catch {
      // The mutation displays the error; resolving keeps the editor open for another save attempt.
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!reportReadyImageId) throw new Error("Missing Report Ready image ID");
      const response = await apiRequest(`/api/report-ready-images/${reportReadyImageId}`, "DELETE");
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Image removed from Report Ready",
        description: "The source image is back in Retailer Ready for a new decision.",
      });
      onSaved();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    },
  });

  const title = mode === "create" ? "Prepare Image for Report" : "Edit Report Image";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="h-[calc(100dvh-1.5rem)] w-[calc(100vw-1.5rem)] max-w-[1600px] grid-rows-[auto_minmax(0,1fr)] overflow-hidden p-3 sm:p-5"
        >
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between gap-4 pr-8">
              <div>
                <DialogTitle>{title}</DialogTitle>
                {(storeName || operatorName) && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {[storeName, operatorName].filter(Boolean).join(" / ")}
                  </p>
                )}
              </div>
              {mode === "edit" && reportReadyImageId && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => setDeleteOpen(true)}
                  disabled={deleteMutation.isPending || saveMutation.isPending}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove from report
                </Button>
              )}
            </div>
            <p className="pr-8 text-xs text-muted-foreground">
              Adjust the image and choose how it appears in the report in one step.
              The full image remains available for repositioning in PowerPoint.
            </p>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-hidden">
            {sourceLoadError ? (
              <div className="flex h-full min-h-[520px] items-center justify-center rounded-md border bg-muted px-6 text-center text-sm text-destructive">
                {sourceLoadError}
              </div>
            ) : !editorSourceUrl ? (
              <div className="flex h-full min-h-[520px] items-center justify-center rounded-md border bg-muted text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading image editor...
              </div>
            ) : (
              <ReportReadyImageEditor
                key={editorKey}
                imageUrl={editorSourceUrl}
                initialAdjustments={initialAdjustments}
                reportCrop={reportCrop}
                saving={saveMutation.isPending}
                onSave={handleSave}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this image from Report Ready?</AlertDialogTitle>
            <AlertDialogDescription>
              The prepared version will be removed from future reports, and the original image will return to Retailer Ready for a new Add, Edit, or Exclude decision. Reports already generated will not change.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? "Removing..." : "Remove from report"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
